import { TAdFoxBannerCompleteSizes } from '@cian/adfox-component';
import { Button, Modal, UIHeading1, UIHeading2, UIHeading4, UIText1, UIHeading5 } from '@cian/ui-kit';
import { IconActionClose12, IconActionMore16 } from '@cian/ui-kit-design-tokens/icons';
import cx from 'classnames';
import * as React from 'react';

import styles from './NewbuildingSpecialProjectAnimated.css';
import { INewbuildingSpecialProjectBannerContext } from '../../../../../../types/adfox';

interface INewbuildingSpecialProjectAnimatedProps {
  sizes: TAdFoxBannerCompleteSizes;
  context: INewbuildingSpecialProjectBannerContext;
}

export function NewbuildingSpecialProjectAnimated({ context, sizes }: INewbuildingSpecialProjectAnimatedProps) {
  const { title, text, url, buttonText, substrateNotNeeded, substrateLogoNotNeeded, disclaimer, logo, bgImage } =
    context;

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const containerRef = React.useRef<HTMLAnchorElement>(null);
  const backgroundRef = React.useRef<HTMLDivElement>(null);
  const rafIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (backgroundRef.current) {
      backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
    }

    const updateParallax = () => {
      if (!containerRef.current || !backgroundRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;
      const elementCenter = elementTop + elementHeight / 2;
      const viewportCenter = windowHeight / 2;

      if (elementTop < windowHeight && elementTop + elementHeight > 0) {
        const distanceFromCenter = (elementCenter - viewportCenter) / windowHeight;
        const parallaxOffset = distanceFromCenter * 80;
        backgroundRef.current.style.transform = `translate3d(0px, ${parallaxOffset}px, 0px)`;
      } else {
        backgroundRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
      }
    };

    const handleScroll = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateParallax);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateParallax();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return (
    <div className={styles['block-wrapper']} data-testid="NewbuildingSpecialProjectAnimated">
      <a
        ref={containerRef}
        href={url}
        target="_blank"
        rel="nofollow noopener noreferrer"
        className={cx(
          styles['container'],
          !Number(substrateNotNeeded) && styles['container--without-substrate'],
          !Number(substrateLogoNotNeeded) && styles['container--without-substratelogo'],
        )}
        style={sizes}
      >
        <div ref={backgroundRef} className={styles['background-layer']}>
          <img src={bgImage} alt="image" className={styles['background-image']} />
        </div>
        <div className={styles['wrapper']}>
          <div className={styles['logo']}>
            <img className={styles['logo-image']} src={logo} alt={title} />
          </div>
          <div className={styles['content']}>
            <div className={styles['info']}>
              <div className={styles['title']}>{title}</div>
              <div className={styles['text']}>
                <UIHeading2 color="text-inverted-default">{text}</UIHeading2>
              </div>
            </div>
            {buttonText && (
              <div className={styles['control']}>
                <div className={styles['control-info-button']}>
                  <button
                    type="button"
                    className={styles['info-button']}
                    onClick={event => {
                      event.preventDefault();
                      setIsModalOpen(true);
                    }}
                  >
                    <UIHeading5 color="text-inverted-default">Реклама</UIHeading5>
                    <span className={styles['info-button-icon']}>
                      <IconActionMore16 color="icon-inverted-default" />
                    </span>
                  </button>
                </div>

                <div className={styles['action-button-wrapper']}>
                  <button type="button" className={styles['action-button']}>
                    <span className={styles['action-button-text']}>
                      <UIHeading4 color="text-inverted-default">{buttonText}</UIHeading4>
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </a>
      {disclaimer && (
        <Modal
          size="S"
          closeButtonProps={{
            theme: 'fill_light',
            size: 'XS',
            icon: <IconActionClose12 color="current_color" />,
            title: 'Закрыть',
          }}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          header={<UIHeading1>Реклама</UIHeading1>}
          body={
            <div>
              <UIText1>{disclaimer}</UIText1>
              <div className={styles['modal-footer']}>
                <Button type="button" theme="fill_primary" onClick={() => setIsModalOpen(false)}>
                  Закрыть
                </Button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
